/**
 * Desktop Mega Menu
 * =================
 *
 * Enhances CSS-based mega menu with keyboard navigation and accessibility features.
 *
 * Features:
 * - Desktop only (>= 1280px) via matchMedia
 * - CSS handles hover/focus-within (no JS hover listeners)
 * - JS adds keyboard controls: Enter/Space, Escape, Arrow keys
 * - Click outside to close
 * - Tab/Shift+Tab handling for natural keyboard flow
 * - ARIA attributes for screen readers
 *
 * Architecture:
 * - Minimal JS footprint (CSS does heavy lifting)
 * - Zero layout thrashing (class-based, no getBoundingClientRect)
 * - Passive event listeners where possible
 * - Debounced resize handlers
 *
 * Browser Support: IE11+ (matchMedia), evergreen browsers
 *
 * @since 2.0.0
 */

import { TIMING, ScrollIndicator } from './menu-utils.js'
import { NAV_DESKTOP_MQ } from './breakpoints.js'

class DesktopMegaMenu {
  constructor () {
    // Store media query matcher
    this.mediaQuery = NAV_DESKTOP_MQ

    // Cache DOM elements
    this.navigation = null
    this.topLevelItems = []
    this.currentOpenItem = null

    // Store click handlers so we can remove them later
    this.clickHandlers = new Map()

    // Scroll indicator instances (one per submenu)
    this.scrollIndicators = new Map()

    // Bind methods to preserve context
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleMediaChange = this.handleMediaChange.bind(this)

    // Initialize
    this.init()
  }

  /**
   * Initialize the mega menu
   */
  init () {
    // Cache DOM elements
    this.navigation = document.querySelector('.site-header__navigation')

    if (!this.navigation) {
      // Navigation not found - fail silently in production
      return
    }

    // Get all top-level menu items (level 0)
    this.topLevelItems = Array.from(
      this.navigation.querySelectorAll('.menu-level-0')
    )

    // Check initial viewport state and add listeners
    this.handleMediaChange(this.mediaQuery)

    // Listen for viewport changes
    // Modern browsers support addEventListener on MediaQueryList
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.handleMediaChange)
    } else {
      // Fallback for older browsers
      this.mediaQuery.addListener(this.handleMediaChange)
    }
  }

  /**
   * Handle viewport changes (enable/disable based on breakpoint)
   *
   * @param {MediaQueryList|MediaQueryListEvent} mq - Media query object
   */
  handleMediaChange (mq) {
    if (mq.matches) {
      // Desktop: Enable mega menu
      this.enable()
    } else {
      // Mobile: Disable mega menu (accordion handled by off-canvas-menu.js)
      this.disable()
    }
  }

  /**
   * Enable desktop mega menu functionality
   */
  enable () {
    // Use MutationObserver to detect when accordion expands/collapses menus
    this.menuObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const item = mutation.target

          // Check if this item became expanded
          if (item.classList.contains('is-expanded')) {
            const subMenuWrapper = item.querySelector(':scope > .sub-menu-wrapper')

            // Wait for accordion animation to complete before checking (similar to mobile)
            setTimeout(() => {
              // Use requestAnimationFrame to ensure layout is complete
              requestAnimationFrame(() => {
                this.checkScrollIndicator(subMenuWrapper, item)
              })
            }, TIMING.ACCORDION_OPEN)
          } else if (mutation.oldValue && mutation.oldValue.includes('is-expanded')) {
            // Item was closed, detach scroll indicator
            this.detachScrollIndicator(item)
          }
        }
      })
    })

    // Observe class changes on all top-level menu items
    this.topLevelItems.forEach(item => {
      this.menuObserver.observe(item, {
        attributes: true,
        attributeOldValue: true,
        attributeFilter: ['class']
      })

      const link = item.querySelector(':scope > a[data-accordion-trigger]')
      if (link) {
        // Keyboard event listeners for accessibility
        link.addEventListener('keydown', this.handleKeydown)
      }
    })

    // Listen for clicks outside menu to close
    document.addEventListener('click', this.handleOutsideClick)
  }

  /**
   * Disable desktop mega menu functionality
   */
  disable () {
    // Disconnect mutation observer
    if (this.menuObserver) {
      this.menuObserver.disconnect()
      this.menuObserver = null
    }

    // Remove keyboard listeners
    this.topLevelItems.forEach(item => {
      const link = item.querySelector(':scope > a[data-accordion-trigger]')
      if (link) {
        link.removeEventListener('keydown', this.handleKeydown)
      }
    })

    // Remove outside click listener
    document.removeEventListener('click', this.handleOutsideClick)

    // Remove media query listener
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.handleMediaChange)
    } else {
      this.mediaQuery.removeListener(this.handleMediaChange)
    }

    // Detach all scroll indicators
    this.scrollIndicators.forEach(indicator => indicator.detach())
    this.scrollIndicators.clear()
  }

  /**
   * Handle keyboard navigation
   *
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleKeydown (event) {
    const { key } = event
    const topLevelItem = event.target.closest('.menu-level-0')
    const link = topLevelItem?.querySelector(':scope > a[data-accordion-trigger]')
    const subMenuWrapper = topLevelItem?.querySelector(':scope > .sub-menu-wrapper')

    // Skip if no submenu
    if (!subMenuWrapper || !link) return

    const isExpanded = topLevelItem.classList.contains('is-expanded')

    // Escape: Close mega menu
    if (key === 'Escape' && isExpanded) {
      event.preventDefault()
      link.click() // Let accordion handle closing
      link.focus()
      return
    }

    // Arrow Down: Open menu and focus first link
    if (key === 'ArrowDown' && !isExpanded) {
      event.preventDefault()
      link.click() // Let accordion handle opening
      setTimeout(() => this.focusFirstLink(subMenuWrapper), 250)
      return
    }

    // Arrow Up: Close menu
    if (key === 'ArrowUp' && isExpanded) {
      event.preventDefault()
      link.click() // Let accordion handle closing
      return
    }

    // Arrow Right: Move to next top-level item
    if (key === 'ArrowRight') {
      event.preventDefault()
      this.focusNextTopLevel(topLevelItem)
      return
    }

    // Arrow Left: Move to previous top-level item
    if (key === 'ArrowLeft') {
      event.preventDefault()
      this.focusPrevTopLevel(topLevelItem)
    }
  }

  /**
   * Handle clicks outside the navigation to close menus
   *
   * @param {MouseEvent} event - Click event
   */
  handleOutsideClick (event) {
    // If click is outside navigation, close all open menus
    if (!this.navigation.contains(event.target)) {
      this.topLevelItems.forEach(item => {
        const link = item.querySelector(':scope > a[data-accordion-trigger]')
        if (link && item.classList.contains('is-expanded')) {
          link.click() // Let accordion handle closing
        }
      })
    }
  }

  /**
   * Attach scroll indicator to a submenu
   *
   * @param {HTMLElement} subMenuWrapper - The submenu wrapper element
   * @param {HTMLElement} item - The menu item element
   */
  attachScrollIndicator (subMenuWrapper, item) {
    if (!subMenuWrapper || !item) return

    // Create new ScrollIndicator instance
    const indicator = new ScrollIndicator(subMenuWrapper, 'show-desktop-scroll-indicator')

    // Store it
    this.scrollIndicators.set(item, indicator)

    // Attach listener
    indicator.attach()
  }

  /**
   * Detach scroll indicator from a submenu
   *
   * @param {HTMLElement} item - The menu item element
   */
  detachScrollIndicator (item) {
    const indicator = this.scrollIndicators.get(item)
    if (indicator) {
      indicator.detach()
      this.scrollIndicators.delete(item)
    }
  }

  /**
   * Check if scroll indicator should be shown for mega menu
   *
   * @param {HTMLElement} subMenuWrapper - The submenu wrapper element
   * @param {HTMLElement} item - The menu item element
   */
  checkScrollIndicator (subMenuWrapper, item) {
    if (!subMenuWrapper || !item) {
      return
    }

    // Get viewport height
    const viewportHeight = window.innerHeight

    // Get header height - use getBoundingClientRect for accurate rendered size
    const header = document.querySelector('.site-header')
    const headerHeight = header ? header.getBoundingClientRect().height : 0

    // Get the actual rendered height of the submenu wrapper
    const wrapperHeight = subMenuWrapper.scrollHeight

    // Calculate available space below the header
    const availableSpace = viewportHeight - headerHeight

    // Show indicator if content is taller than available space
    // Add small buffer (10px) to avoid flickering on edge cases
    if (wrapperHeight > availableSpace - 10) {
      this.attachScrollIndicator(subMenuWrapper, item)
    } else {
      this.detachScrollIndicator(item)
    }
  }

  /**
   * Focus first link in submenu
   *
   * @param {HTMLElement} subMenuWrapper - Submenu wrapper element
   */
  focusFirstLink (subMenuWrapper) {
    const firstLink = subMenuWrapper.querySelector('a')

    if (firstLink) {
      firstLink.focus()
    }
  }

  /**
   * Focus next top-level item
   *
   * @param {HTMLElement} currentItem - Current top-level item
   */
  focusNextTopLevel (currentItem) {
    const currentIndex = this.topLevelItems.indexOf(currentItem)
    const nextIndex = (currentIndex + 1) % this.topLevelItems.length
    const nextItem = this.topLevelItems[nextIndex]
    const nextLink = nextItem?.querySelector(':scope > a')

    if (nextLink) {
      nextLink.focus()
    }
  }

  /**
   * Focus previous top-level item
   *
   * @param {HTMLElement} currentItem - Current top-level item
   */
  focusPrevTopLevel (currentItem) {
    const currentIndex = this.topLevelItems.indexOf(currentItem)
    const prevIndex = currentIndex - 1 < 0 ? this.topLevelItems.length - 1 : currentIndex - 1
    const prevItem = this.topLevelItems[prevIndex]
    const prevLink = prevItem?.querySelector(':scope > a')

    if (prevLink) {
      prevLink.focus()
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.thsDesktopMegaMenu = new DesktopMegaMenu()
  })
} else {
  // DOM already loaded
  window.thsDesktopMegaMenu = new DesktopMegaMenu()
}

export default DesktopMegaMenu
